import { useEffect, useState } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "sonner";

import Intro from "./components/Intro";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import MerchSection from "./components/MerchSection";
import CategoryShowcase from "./components/CategoryShowcase";
import Tour from "./components/Tour";
import AlbumExperience from "./components/AlbumExperience";
import NewsPreview from "./components/NewsPreview";
import EmailSignup from "./components/EmailSignup";
import Footer from "./components/Footer";
import WelcomePopup from "./components/WelcomePopup";
import PrivacyPolicy from "./components/PrivacyPolicy";
import Terms from "./components/Terms";
import ProductDetail from "./components/ProductDetail";
import CollectionPage from "./components/CollectionPage";
import CartPage, { CartCrossSell } from "./components/CartPage";
import PopupExperience from "./components/PopupExperience";
import NewMusic from "./components/NewMusic";
import BookFreeway from "./components/BookFreeway";
import { NewsIndex, NewsArticle } from "./components/News";
import AdminSubscribers from "./components/AdminSubscribers";
import { CartProvider } from "./lib/cart";
import { enterAlbumExperience } from "./lib/experience";

// Versioned session key — bumped in the correction pass so any previously
// stored "dismissed" state does NOT suppress the corrected popup during test.
const WELCOME_SESSION_KEY = "fta_welcome_v3";
const INTRO_SESSION_KEY = "fta_planes_intro_v3";

// Homepage sections. Album Experience is intentionally NOT rendered here anymore —
// it lives on the dedicated /album route only. Nav "Explore the Album" opens /album.
function Home() {
  return (
    <div className="App relative grain bg-black text-white">
      <Navbar />
      <main>
        <Hero />
        <MerchSection />
        <CategoryShowcase />
        <Tour />
        <NewsPreview />
        <EmailSignup />
      </main>
      <Footer />
    </div>
  );
}

// Dedicated /album page — renders the Album Experience surface only, with the
// booklet accordion auto-opened. Booklet, Vault, Tracklist, QR, and Operation
// Philadelphia Freeway logic all preserved from the shared component.
function AlbumPage() {
  useEffect(() => {
    const t1 = setTimeout(enterAlbumExperience, 150);
    const t2 = setTimeout(enterAlbumExperience, 900);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);
  return (
    <div className="App relative grain bg-black text-white">
      <Navbar />
      <main>
        <AlbumExperience />
      </main>
      <Footer />
    </div>
  );
}

// Scroll-restoration for standard route navigation. Every route change resets
// the viewport to the top so product/collection/news/cart/inquiry pages open
// at 0 — never mid-page. In-page anchor scrolling (e.g. Hero → #categories) is
// unaffected because it is triggered by user click after the page mounts.
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Skip the /album deep-link — that route intentionally scrolls to
    // the Explore the Album section after mount.
    if (pathname === "/album") return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);
  return null;
}

function AppShell() {
  const location = useLocation();
  const isHome = location.pathname === "/";
  const [introDone, setIntroDone] = useState(() => sessionStorage.getItem(INTRO_SESSION_KEY) === "1" || !isHome);
  const [welcomeOpen, setWelcomeOpen] = useState(() => isHome && sessionStorage.getItem(WELCOME_SESSION_KEY) !== "1");

  const finishIntro = () => { sessionStorage.setItem(INTRO_SESSION_KEY, "1"); setIntroDone(true); };
  const dismissWelcome = () => { sessionStorage.setItem(WELCOME_SESSION_KEY, "1"); setWelcomeOpen(false); };

  return (
    <>
      <ScrollToTop />
      <Toaster theme="dark" position="top-center" richColors />
      <AnimatePresence>
        {isHome && !introDone && <Intro key="intro" onFinish={finishIntro} />}
      </AnimatePresence>
      {isHome && welcomeOpen && introDone && <WelcomePopup onClose={dismissWelcome} />}
      <CartCrossSell />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/album" element={<AlbumPage />} />
        <Route path="/product/:slug" element={<ProductDetail />} />
        <Route path="/collection/:collection" element={<CollectionPage />} />
        <Route path="/pop-up-experience" element={<PopupExperience />} />
        <Route path="/new-music" element={<NewMusic />} />
        <Route path="/book-freeway" element={<BookFreeway />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/news" element={<NewsIndex />} />
        <Route path="/news/:slug" element={<NewsArticle />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/admin/subscribers" element={<AdminSubscribers />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <CartProvider>
        <AppShell />
      </CartProvider>
    </BrowserRouter>
  );
}

export default App;
